import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  Gift, Users, Clock, Copy, Check, Link2, TrendingUp, UserCheck, UserPlus, Calendar
} from "lucide-react";

const AmbassadorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/ambassador/dashboard`, { credentials: "include" });
      if (res.ok) {
        setData(await res.json());
      } else if (res.status === 403) {
        navigate("/dashboard/jobseeker");
        toast.error("Ambassador access required");
      }
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!data?.promo_link) return;
    try {
      await navigator.clipboard.writeText(data.promo_link);
      setCopied(true);
      toast.success("Promo link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Sidebar />
        <main className="lg:ml-64 p-6 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  if (!data) return null;

  const expiryPercent = data.duration_months > 0
    ? Math.max(0, Math.min(100, (data.days_remaining / (data.duration_months * 30)) * 100))
    : 0;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="ambassador-dashboard">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold">
            AMBASSADOR <span className="text-crimson">HUB</span>
          </h1>
          <p className="text-gray-400 text-sm mt-1">Track your impact and share Rekrutor with your network</p>
        </motion.div>

        {/* Expiry Countdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-obsidian-light border border-[#222] p-6 mb-6"
          data-testid="expiry-section"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-crimson" />
              <h2 className="font-heading text-lg font-bold">ACCESS COUNTDOWN</h2>
            </div>
            <span className={`text-sm font-bold ${data.days_remaining > 30 ? "text-emerald-400" : data.days_remaining > 7 ? "text-amber-400" : "text-red-400"}`}>
              {data.days_remaining} days left
            </span>
          </div>
          <div className="h-2 bg-[#1a1a1a] w-full mb-2">
            <div
              className={`h-full transition-all ${data.days_remaining > 30 ? "bg-emerald-500" : data.days_remaining > 7 ? "bg-amber-500" : "bg-red-500"}`}
              style={{ width: `${expiryPercent}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Started {data.granted_at ? new Date(data.granted_at).toLocaleDateString() : ""}</span>
            <span>Expires {data.expires_at ? new Date(data.expires_at).toLocaleDateString() : ""}</span>
          </div>
        </motion.div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { icon: UserPlus, label: "Total Referrals", value: data.total_referrals, color: "text-crimson" },
            { icon: UserCheck, label: "Active Users", value: data.active_referrals, color: "text-emerald-400" },
            { icon: Clock, label: "Days Left", value: data.days_remaining, color: data.days_remaining > 30 ? "text-emerald-400" : "text-amber-400" },
            { icon: TrendingUp, label: "Conversion", value: data.total_referrals > 0 ? `${Math.round((data.active_referrals / data.total_referrals) * 100)}%` : "0%", color: "text-blue-400" },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }}
              className="bg-obsidian-light border border-[#222] p-4"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Promo Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-obsidian-light border border-crimson/30 p-6 mb-6"
          data-testid="promo-link-section"
        >
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-crimson" />
            <h2 className="font-heading text-lg font-bold">YOUR PROMO LINK</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">Share this link with your network. Anyone who signs up through it will be tracked as your referral.</p>
          <div className="flex gap-2">
            <div className="flex-1 bg-obsidian border border-[#333] px-4 py-2.5 text-sm text-gray-300 truncate">
              {data.promo_link}
            </div>
            <Button
              onClick={copyLink}
              className={`shrink-0 ${copied ? "bg-emerald-600" : "bg-crimson hover:bg-crimson/80"}`}
              data-testid="copy-promo-link"
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Promo code: <span className="text-white font-mono">{data.promo_code}</span>
          </div>
        </motion.div>

        {/* Referred Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          data-testid="referrals-table"
        >
          <h2 className="font-heading text-xl font-bold mb-4">
            REFERRED <span className="text-crimson">USERS</span>
          </h2>
          {data.referred_users?.length > 0 ? (
            <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#333]">
                    <th className="text-left p-3 text-gray-400 text-xs uppercase">User</th>
                    <th className="text-left p-3 text-gray-400 text-xs uppercase">Role</th>
                    <th className="text-left p-3 text-gray-400 text-xs uppercase">Plan</th>
                    <th className="text-left p-3 text-gray-400 text-xs uppercase">Status</th>
                    <th className="text-left p-3 text-gray-400 text-xs uppercase">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referred_users.map((u, i) => (
                    <tr key={i} className="border-b border-[#1a1a1a] last:border-0">
                      <td className="p-3">
                        <div className="text-sm text-white">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </td>
                      <td className="p-3 text-sm text-gray-300 capitalize">{u.role || "-"}</td>
                      <td className="p-3 text-sm text-gray-300">{u.subscription_tier || "free"}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 uppercase ${
                          u.status === "converted" ? "bg-emerald-500/20 text-emerald-400" : "bg-gray-500/20 text-gray-400"
                        }`}>{u.status}</span>
                      </td>
                      <td className="p-3 text-xs text-gray-500">
                        {u.joined_at ? new Date(u.joined_at).toLocaleDateString() : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm mb-2">No referrals yet</p>
              <p className="text-gray-500 text-xs">Share your promo link to start tracking referrals</p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default AmbassadorDashboard;
